import { useEffect } from "react";
import { Link, useLocation } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { db } from "../supabase";
import { fehlertext } from "../fehler";
import { useAlleGelesen, useMitteilungen } from "../mitteilungen";
import { useAngemeldet, useRecht } from "../sitzung";
import { MitteilungenListe } from "../shell/MitteilungenListe";
import { Karte } from "../ui/Karte";
import { Knopf } from "../ui/Knopf";
import { Leer, Meldung } from "../ui/Leer";
import { Schalter } from "../ui/Schalter";
import { useHinweis } from "../ui/Hinweise";
import s from "./seiten.module.css";
import { NeueAufgabeKnopf } from "../ui/NeueAufgabeKnopf";
import { Laedt } from "../ui/Laedt";

/** Anlässe mit Mail und Push (KONZEPT.md, Benachrichtigungen). Die App lässt sich nicht abschalten. */
const ANLAESSE = [
  { anlass: "zugewiesen", label: "Dir wurde eine Aufgabe gegeben oder du wurdest hinzugefügt", mail: true, push: true },
  { anlass: "termin", label: "Sitzung verschoben oder abgesagt", mail: true, push: false },
  { anlass: "protokoll", label: "Protokoll nach der Sitzung", mail: true, push: false },
  { anlass: "ueberfaellig", label: "Überfällig: am Tag danach, dann wöchentlich", mail: true, push: true },
  { anlass: "wochenuebersicht", label: "Wochenübersicht montags um 8 Uhr", mail: true, push: false },
  { anlass: "frist_morgen", label: "Frist morgen", mail: false, push: true },
  { anlass: "erwaehnt", label: "Erwähnt in einem Kommentar", mail: false, push: true },
] as const;
type Anlass = (typeof ANLAESSE)[number]["anlass"];

/** Ansicht 06: alle Mitteilungen auf einer Seite, Einstellungen je Anlass (Anker #einstellungen, Ziel von „Benachrichtigungen“ im
 *  Personenmenü), für Admins der Test von Mail und Slack. Die Liste ist dieselbe wie in der Leiste rechts (KOPFZEILE.md, Abschnitt 3.4):
 *  Ein Klick setzt genau diese Mitteilung gelesen, eine Aufgabe öffnet als Dialog über dieser Seite. */
export function Mitteilungen() {
  const liste = useMitteilungen();
  const gelesen = useAlleGelesen();
  const ort = useLocation();

  // „Benachrichtigungen“ im Personenmenü: auch dann zu den Einstellungen, wenn die Seite schon offen ist
  useEffect(() => {
    if (ort.hash === "#einstellungen") document.getElementById("einstellungen")?.scrollIntoView({ block: "start" });
  }, [ort.key, ort.hash]);

  const ungelesen = (liste.data ?? []).filter((m) => !m.gelesen_am).length;
  const admin = useRecht("zugaenge.verwalten");

  return (
    <div className={s.stapel}>
      <div className={s.kopf}>
        <div>
          <h1>Mitteilungen.</h1>
          <p>{ungelesen > 0 ? `${ungelesen} ungelesen` : "Alles gelesen"}</p>
        </div>
        <div className={s.zeile}>
          {ungelesen > 0 ? (
            <Knopf onClick={() => gelesen.mutate()} disabled={gelesen.isPending}>
              Alle als gelesen markieren
            </Knopf>
          ) : null}
          <NeueAufgabeKnopf />
        </div>
      </div>
      <Karte>
        {liste.isPending ? <Laedt /> : null}
        {liste.isError ? <Meldung fehler>{fehlertext(liste.error)}</Meldung> : null}
        {liste.data ? (
          <MitteilungenListe liste={liste.data} ueber="seite" leer={<Leer titel="Noch keine Mitteilungen.">Hier erscheint, was andere für dich tun oder ändern.</Leer>} />
        ) : null}
      </Karte>
      <Einstellungen />
      {admin ? <VersandPruefen /> : null}
    </div>
  );
}

function Einstellungen() {
  const qc = useQueryClient();
  const { zeigen } = useHinweis();
  const ich = useAngemeldet().person.id;
  const einstellungen = useQuery({
    queryKey: ["mitteilungen", "einstellungen", ich],
    queryFn: async () => {
      const { data, error } = await db().from("app_benachrichtigungen").select("anlass, mail, push");
      if (error) throw error;
      return new Map(data.map((e) => [e.anlass, { mail: e.mail, push: e.push }]));
    },
  });
  const setzen = useMutation({
    mutationFn: async ({ anlass, kanal, an }: { anlass: Anlass; kanal: "mail" | "push"; an: boolean }) => {
      const jetzt = einstellungen.data?.get(anlass) ?? { mail: true, push: true };
      const neu = { ...jetzt, [kanal]: an };
      const { error } = await db().rpc("app_benachrichtigung_setzen", { p_anlass: anlass, p_mail: neu.mail, p_push: neu.push });
      if (error) throw error;
      return { kanal, an };
    },
    onSuccess: ({ kanal, an }) => zeigen(`${kanal === "mail" ? "Mail" : "Push"} ist ${an ? "wieder an." : "aus. In der App siehst du es weiter."}`),
    onError: (e) => zeigen(fehlertext(e), { fehler: true }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["mitteilungen", "einstellungen"] }),
  });

  return (
    <Karte titel="Einstellungen" id="einstellungen">
      <p>
        In der App siehst du alles, was dich betrifft. Mail und Push kannst du je Anlass abschalten. Zwischen 21 und 8 Uhr kommt keine Mail und keine Push, sie warten bis 8 Uhr.
        Push richtest du je Gerät im <Link to="/profil#push">Profil</Link> ein.
      </p>
      {einstellungen.isError ? <Meldung fehler>{fehlertext(einstellungen.error)}</Meldung> : null}
      <ul className={s.einstellungen}>
        {ANLAESSE.map((a) => (
          <li key={a.anlass}>
            <span>{a.label}</span>
            <span className={s.kanaele}>
              {a.mail ? (
                <Schalter
                  label="Mail"
                  aria-label={`Mail: ${a.label}`}
                  checked={einstellungen.data?.get(a.anlass)?.mail ?? true}
                  disabled={!einstellungen.data || setzen.isPending}
                  onChange={(e) => setzen.mutate({ anlass: a.anlass, kanal: "mail", an: e.target.checked })}
                />
              ) : null}
              {a.push ? (
                <Schalter
                  label="Push"
                  aria-label={`Push: ${a.label}`}
                  checked={einstellungen.data?.get(a.anlass)?.push ?? true}
                  disabled={!einstellungen.data || setzen.isPending}
                  onChange={(e) => setzen.mutate({ anlass: a.anlass, kanal: "push", an: e.target.checked })}
                />
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      <p className={s.hinweistext}>Push kommt nur auf Geräten an, die du im Profil eingerichtet hast (Karte „Push auf dem Handy“).</p>
    </Karte>
  );
}

async function versandTesten(kanal: "mail" | "slack"): Promise<string> {
  const { data } = await db().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Bitte melde dich neu an.");
  const res = await fetch("/.netlify/functions/versand-test", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ kanal }),
  });
  const j = (await res.json().catch(() => ({}))) as { ok?: boolean; fehler?: string; ziel?: string };
  if (!res.ok || !j.ok) throw Object.assign(new Error(j.fehler ?? "Der Test hat nicht geklappt."), { code: "P0001" });
  return j.ziel ?? "";
}

/** Nur Admins: Testmail an die eigene Adresse und Test-Post nach Slack */
function VersandPruefen() {
  const { zeigen } = useHinweis();
  const test = useMutation({
    mutationFn: versandTesten,
    onSuccess: (ziel, kanal) => zeigen(kanal === "mail" ? "Testmail ist unterwegs an deine Adresse." : `Test-Post ist raus: ${ziel}.`),
    onError: (e) => zeigen(fehlertext(e), { fehler: true }),
  });
  return (
    <Karte titel="Versand prüfen">
      <p>Nur für Admins. Die Testmail geht an deine Adresse, der Test-Post in den Slack-Testkanal (ohne Testkanal nach #7_vorstand).</p>
      <p>
        Für Push brauchst du einmal drei Werte in Netlify: <Link to="/push-schluessel">Push-Schlüssel erzeugen</Link>, ohne Terminal.
      </p>
      <div className={s.zeile}>
        <Knopf onClick={() => test.mutate("mail")} disabled={test.isPending}>
          Testmail an mich
        </Knopf>
        <Knopf onClick={() => test.mutate("slack")} disabled={test.isPending}>
          Test nach Slack
        </Knopf>
      </div>
    </Karte>
  );
}
